import React, { useEffect, useRef } from 'react';
import { useOrderMessages } from '../../hooks/collections/useOrderMessages';
import { useUserSessionStore } from '../../stores/UserSession.store';
import ButtonPrimary from '../../shared/Button/ButtonPrimary';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import Textarea from '../../shared/Textarea/Textarea';
import { useAlert } from '../../@shared/hooks/useAlert';
import { UserTypeEnum } from '../../@core/enums/UserType.enum';
import ButtonSecondary from '../../shared/Button/ButtonSecondary';
import { useReservations } from '../../hooks/collections/useReservations';

interface OrderChatProps {
  reservationId: string;
  onResolveChat?: () => void;
}

const OrderChat: React.FC<OrderChatProps> = ({ reservationId, onResolveChat }) => {
  const { messages, addMessage, fetched } = useOrderMessages({ loadData: true, reservationId });
  const user = useUserSessionStore((state) => state.user);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const alert = useAlert();
  const { updateReservation } = useReservations({ loadData: false });
  const { handleSubmit, register, formState: { isSubmitting }, setValue } = useForm({
    defaultValues: {
      message: ''
    }
  });

  useEffect(() => {
    // Don't use scrollIntoView as it can affect the entire page scroll
    // Instead, directly scroll the parent container which has overflow-y: auto
    const chatContainer = messagesEndRef.current?.closest('.overflow-y-auto');
    if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
  }, [messages]);

  const submitMessage = async (data: any) => {
    if (!data.message) return;
    if (data.message.trim() === '') return;

    try {
      await addMessage({
        message: data.message,
        role: user?.role,
        name: user?.name
      });
      setValue('message', '');
    } catch (error) {
      console.error('Error sending message:', error);
      alert.error('Error', 'Unable to send message');
    }
  };

  const handleDoneChat = async () => {
    if (!await alert.confirm('Mark completed chat', 'The chat will appear again after the customer answers again.')) return;
    try {
      await updateReservation(reservationId, { newMessages: false });
      onResolveChat && onResolveChat();
    } catch (e) {
      console.error('Error marking chat as done:', e);
    }
  };

  return (
    <div className="shadow-sm p-4 h-full flex flex-col">
      <div className="flex-grow overflow-y-auto mb-4 p-2 max-h-full">
        {!fetched ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-neutral-500 dark:text-neutral-400">
            No messages yet. Start the conversation!
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.createdBy.id === user?.id ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    msg.createdBy.id === user?.id
                      ? 'bg-primary-100 dark:bg-primary-900/50'
                      : 'bg-neutral-100 dark:bg-neutral-800'
                  }`}
                >
                  <div className="flex items-center mb-1">
                      <span className="font-medium text-sm">
                        {msg?.name} ({msg?.role})
                      </span>
                  </div>
                  <p className="text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap break-words">
                    {msg.message}
                  </p>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 text-right">
                    {moment(msg.createdAt).format('MMM D, YYYY h:mm A')}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef}/>
          </div>
        )}
      </div>

      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4 flex-shrink-0">
        <form onSubmit={handleSubmit(submitMessage)} className="flex items-end">
          <Textarea
            {...register('message', {
              required: true
            })}
            placeholder="Type your message..."
            rows={3}
            className={'resize-none'}
            disabled={isSubmitting}
          />
          <div className={'flex flex-col ml-2 gap-2'}>
            {(user?.role === UserTypeEnum.COMPANY || user?.role === UserTypeEnum.PROVIDER) && (
              <ButtonSecondary
                onClick={handleDoneChat}
                type={'button'}
                className={'!h-8'}
              >
                Done
              </ButtonSecondary>
            )}
            <ButtonPrimary
              className={'!h-8'}
              type={'submit'}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <span>Send</span>
              )}
            </ButtonPrimary>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderChat;
